/**
 * ExecutorProgressView — shown to executors before death is verified (the PRE-death view).
 *
 * Tier 1: progress/completion only (no actual data content)
 * Tier 2: same view + browse the plan
 * Any Primary executor gets the "Report Death" button (s127 — no longer tier-gated).
 *
 * Once death is verified this view redirects to the full estate dashboard regardless of tier
 * (s127 fix; see lib/executor-access.ts). Fetches all its own data from clientId prop.
 */

import { useQuery } from "@tanstack/react-query";
import { useDeathEvent } from "@/lib/use-death-event";
import { useEffect } from "react";
import { canAccessFullEstate, primaryCanReportDeath } from "@/lib/executor-access";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Circle, Clock, Shield } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";

export interface ProgressCategory {
  id: string;
  name: string;
  slug: string;
  completed: number;
  total: number;
}

/**
 * Derive overall + per-category stats from the /api/unified/progress response,
 * which is a BARE ARRAY (not a wrapped { categories, overallTotal, ... } object).
 * Null-safe: undefined/empty → zeros. Exported to unit-test the array-vs-object
 * shape contract (the s74 crash was `progress?.categories.map` on the bare array).
 */
export function summarizeProgress(progress: ProgressCategory[] | undefined) {
  const categories = progress ?? [];
  const overallTotal = categories.reduce((sum, c) => sum + c.total, 0);
  const overallCompleted = categories.reduce((sum, c) => sum + c.completed, 0);
  const overallPct = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0;
  return { categories, overallTotal, overallCompleted, overallPct };
}

interface ExecutorProgressViewProps {
  clientId: string;
}

function CompletionRing({ percent }: { percent: number }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  const color = percent >= 80 ? "#38A169" : percent >= 40 ? "#1ABC9C" : "#718096";

  return (
    <svg width="100" height="100" className="transform -rotate-90">
      <circle cx="50" cy="50" r={radius} stroke="#E2E8F0" strokeWidth="10" fill="none" />
      <circle
        cx="50"
        cy="50"
        r={radius}
        stroke={color}
        strokeWidth="10"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.5s ease" }}
      />
    </svg>
  );
}

export default function ExecutorProgressView({ clientId }: ExecutorProgressViewProps) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  // Fetch executor access records for this client — find this user's record
  const { data: executorAccesses } = useQuery<any[]>({
    queryKey: ["/api/clients", clientId, "executors"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/executors`);
      if (!res.ok) throw new Error("Failed to fetch executors");
      return res.json();
    },
    enabled: !!clientId,
  });
  const myAccess = executorAccesses?.find((e: any) => e.userId === user?.id);
  // D-217.14 (s218, access-spec R4): a row with no tier is None, not Overview.
  const sharingTier: number = myAccess?.sharingTier ?? 0;
  const isPrimary: boolean = myAccess?.isPrimary ?? false;

  // Fetch active death event
  const { data: deathEvent } = useDeathEvent(clientId);

  // Fetch progress stats. NOTE: /api/unified/progress returns a BARE ARRAY of
  // category-progress rows — NOT a wrapped { categories, overallTotal, ... } object.
  const { data: progress, isLoading } = useQuery<ProgressCategory[]>({
    queryKey: ["/api/unified/progress", clientId],
    queryFn: async () => {
      const res = await fetch(`/api/unified/progress?clientId=${clientId}`);
      if (!res.ok) throw new Error("Failed to fetch progress");
      return res.json();
    },
    enabled: !!clientId,
  });
  const { categories, overallTotal, overallCompleted, overallPct } = summarizeProgress(progress);
  // s162 (LOADING-STATE-HONESTY-1): only claim a number once the data is actually here. `progress`
  // undefined (loading OR errored) must never render as 0% — a percentage is a claim about the
  // estate, and we do not have one yet.
  const hasProgress = !isLoading && Array.isArray(progress);

  // Fetch planner name from contexts
  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
  });
  const myContext = contexts?.executorFor?.find((c: any) => c.clientId === clientId);
  const plannerName = myContext?.plannerName ?? "the plan owner";


  // Auto-redirect to dashboard when death is verified. s127 fix: gate on verification ALONE —
  // a pre-death "Progress only" tier must not keep the executor stranded in this view post-death.
  useEffect(() => {
    if (canAccessFullEstate(deathEvent?.status === "verified")) {
      setLocation(`/executor/${clientId}/dashboard`);
    }
  }, [deathEvent?.status, clientId, setLocation]);

  const isGracePeriod = deathEvent?.status === "pending_grace_period";
  const gracePeriodEndsAt = deathEvent?.gracePeriodEndsAt
    ? new Date(deathEvent.gracePeriodEndsAt)
    : null;
  // s127: NOT tier-gated — "Progress only" is a pre-death viewing choice, not a licence to
  // strand the estate by blocking the one action that unlocks settlement. isPrimary only.
  const canReportDeath = primaryCanReportDeath(isPrimary, !!deathEvent);

  return (
    <div className="p-6 space-y-6 max-w-2xl mx-auto">
      {/* Grace period banner */}
      {isGracePeriod && gracePeriodEndsAt && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Death reported — grace period active</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Full access unlocks on{" "}
              {gracePeriodEndsAt.toLocaleDateString("en-CA", {
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
              . View the waiting screen for countdown details.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation(`/executor/${clientId}/waiting`)}
            >
              View Details
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{plannerName}'s Estate Plan</h1>
          <p className="text-muted-foreground mt-1">
            {sharingTier === 1
              ? "You can see how complete this plan is, but not its contents."
              : "You have read access to this plan. You can browse it now, or wait until it's needed."}
          </p>
          {sharingTier >= 2 && !deathEvent && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setLocation(`/executor/${clientId}/estate`)}
            >
              View Estate Plan
            </Button>
          )}
        </div>
        {isPrimary && (
          <Badge variant="outline" className="shrink-0 gap-1 border-primary/40 text-primary">
            <Shield className="w-3.5 h-3.5" />
            Primary Executor
          </Badge>
        )}
      </div>

      {/* Overall completion ring */}
      <Card>
        <CardContent className="flex items-center gap-6 p-6">
          {/* s162 (LOADING-STATE-HONESTY-1) — say nothing rather than say 0%.
              Before data resolves, `summarizeProgress([])` yields overallPct = 0, so this card
              rendered a confident "0%" ring and "Plan is in early stages" about an estate it had
              not read yet. On a full page load that is the FIRST thing an executor sees — the app
              telling them the plan they were sent to settle is empty. A neutral loading state is
              the honest answer: an unknown is not a zero. */}
          <div className="relative shrink-0">
            <CompletionRing percent={hasProgress ? overallPct : 0} />
            <div className="absolute inset-0 flex items-center justify-center">
              {hasProgress
                ? <span className="text-xl font-bold" data-testid="progress-pct">{overallPct}%</span>
                : <span className="text-xs text-muted-foreground" data-testid="progress-pct-loading">—</span>}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-lg font-semibold" data-testid="progress-stage">
              {!hasProgress
                ? "Loading the estate plan…"
                : overallPct >= 80
                ? "Plan is well prepared"
                : overallPct >= 40
                ? "Plan is in progress"
                : "Plan is in early stages"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {hasProgress
                ? `${overallCompleted} of ${overallTotal} fields completed`
                : "One moment"}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Category breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Plan Sections</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
          ) : (
            categories.map((cat) => {
              const pct = cat.total > 0 ? Math.round((cat.completed / cat.total) * 100) : 0;
              return (
                <div key={cat.id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {pct >= 100 ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                      <span className="font-medium">{cat.name}</span>
                    </div>
                    <span className="text-muted-foreground text-xs">{pct}%</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Report death action */}
      {canReportDeath && (
        <Card className="border-dashed">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div>
              <p className="font-medium text-sm">Report a death</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                If {plannerName} has passed away, you can report it here to begin the verification process.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation(`/executor/${clientId}/report-death`)}
              data-testid="button-report-death"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Report Death
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
